import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderOpen, FileText, MessageSquare, CreditCard, ArrowRight, Check, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    api.get("/requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  }, []);

  const sub = user?.subscription || {};
  const stats = [
    { label: "Pratiche attive", value: requests.length, icon: FolderOpen, to: "/area/pratiche", color: "#10B981" },
    { label: "Documenti", value: docs.length, icon: FileText, to: "/area/documenti", color: "#0A192F" },
    { label: "Richieste utilizzate", value: `${sub.requests_used || 0} di ${sub.requests_total || 3}`, icon: CreditCard, to: "/area/piano", color: "#F97316" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold text-[#0A192F]">Ciao, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="mt-1 text-slate-600">Ecco una panoramica della tua Area Clienti ViviTranquillo.</p>
      </div>

      {/* Plan banner */}
      <div className={`rounded-2xl border p-6 ${sub.active ? "border-[#10B981]/30 bg-[#10B981]/5" : "border-[#F97316]/30 bg-[#F97316]/5"}`}>
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            {sub.active ? <Check className="h-6 w-6 text-[#10B981]" /> : <AlertCircle className="h-6 w-6 text-[#F97316]" />}
            <div>
              <p className="font-display font-bold text-[#0A192F]">
                {sub.active ? "Piano ViviTranquillo attivo" : "Piano ViviTranquillo non attivo"}
              </p>
              <p className="text-sm text-slate-600">
                {sub.active ? "Hai accesso ai servizi inclusi nel tuo piano annuale." : "Attiva il Piano €49/anno per sbloccare il supporto credito."}
              </p>
            </div>
          </div>
          {!sub.active && (
            <Link to="/area/piano" data-testid="dashboard-activate-plan" className="rounded-full bg-[#10B981] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#059669]">
              Attiva ora
            </Link>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-5 sm:grid-cols-3">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} data-testid={`dashboard-stat-${s.label.toLowerCase().replace(/[^a-z]/g, "")}`} className="group rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
            <div className="flex items-center justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ backgroundColor: `${s.color}1a` }}>
                <s.icon className="h-5 w-5" style={{ color: s.color }} />
              </span>
              <ArrowRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-1" />
            </div>
            <p className="mt-4 font-display text-3xl font-extrabold text-[#0A192F]">{s.value}</p>
            <p className="text-sm text-slate-500">{s.label}</p>
          </Link>
        ))}
      </div>

      {/* Service shortcuts */}
      <div>
        <h2 className="font-display text-xl font-bold text-[#0A192F]">I nostri servizi</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Link to="/area/credito" data-testid="dashboard-shortcut-credito" className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-[#10B981]">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0A192F]/5"><FolderOpen className="h-5 w-5 text-[#0A192F]" /></span>
            <div><p className="font-semibold text-[#0A192F]">Servizi Credito</p><p className="text-sm text-slate-500">CRIF, CTC, Banca d'Italia e supporto credito</p></div>
          </Link>
        </div>
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="font-display text-xl font-bold text-[#0A192F]">Azioni rapide</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Link to="/area/richieste" data-testid="dashboard-quick-request" className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-[#10B981]">
            <FolderOpen className="h-6 w-6 text-[#10B981]" />
            <div><p className="font-semibold text-[#0A192F]">Apri una nuova richiesta</p><p className="text-sm text-slate-500">Utenze e supporto credito</p></div>
          </Link>
          <Link to="/area/messaggi" data-testid="dashboard-quick-message" className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-[#10B981]">
            <MessageSquare className="h-6 w-6 text-[#10B981]" />
            <div><p className="font-semibold text-[#0A192F]">Scrivi al team</p><p className="text-sm text-slate-500">Hai bisogno di aiuto? Contattaci.</p></div>
          </Link>
        </div>
      </div>
    </div>
  );
}
